import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  MaintenanceReservation,
  MaintenanceSchedulingService
} from './maintenance-scheduling.service';

@Component({
  selector: 'app-maintenance-reservations',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './maintenance-reservations.view.html',
  styleUrls: ['./maintenance-reservations.view.css']
})
export class MaintenanceReservationsComponent implements OnInit, OnDestroy {
  mechanicName = '';
  reservations: MaintenanceReservation[] = [];
  loading = false;
  errorMessage = '';
  processingReservationId: number | null = null;
  private routeSub: Subscription | null = null;
  private reservationsSub: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly schedulingService: MaintenanceSchedulingService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe((params) => {
      const mechanicFromRoute = (params.get('mechanic') ?? '').trim();
      this.mechanicName = mechanicFromRoute;
      this.loadReservations();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.reservationsSub?.unsubscribe();
  }

  logout(): void {
    try {
      localStorage.removeItem('autoInsight.username');
      localStorage.removeItem('autoInsight.role');
      localStorage.removeItem('autoInsight.ownerId');
      localStorage.removeItem('autoInsight.vehicleId');
    } catch {
      // Ignore storage limitations in private browser contexts.
    }

    this.router.navigate(['/login']);
  }

  openMaintenanceParts(item: MaintenanceReservation): void {
    const queryParams: Record<string, string | number> = {
      ownerId: item.ownerId,
      source: 'reservations'
    };
    if (item.vehicleId !== undefined) {
      queryParams['vehicleId'] = item.vehicleId;
    }
    if (this.mechanicName) {
      queryParams['mechanic'] = this.mechanicName;
    }

    this.router.navigate(['/maintenance-parts'], { queryParams });
  }

  approve(item: MaintenanceReservation): void {
    if (item.status !== 'Pending Approval') {
      return;
    }

    this.processingReservationId = item.id;
    this.reservationsSub?.unsubscribe();
    this.reservationsSub = this.schedulingService.updateReservationStatus(item.id, true).subscribe({
      next: () => {
        this.processingReservationId = null;
        this.loadReservations();
      },
      error: () => {
        this.processingReservationId = null;
        this.errorMessage = 'Unable to update reservation.';
      }
    });
  }

  deny(item: MaintenanceReservation): void {
    if (item.status !== 'Pending Approval') {
      return;
    }

    this.processingReservationId = item.id;
    this.reservationsSub?.unsubscribe();
    this.reservationsSub = this.schedulingService.updateReservationStatus(item.id, false).subscribe({
      next: () => {
        this.processingReservationId = null;
        this.loadReservations();
      },
      error: () => {
        this.processingReservationId = null;
        this.errorMessage = 'Unable to update reservation.';
      }
    });
  }

  private loadReservations(): void {
    this.loading = true;
    this.errorMessage = '';
    this.reservationsSub?.unsubscribe();
    this.reservationsSub = this.schedulingService.getReservationsForMechanic(this.mechanicName).subscribe({
      next: (reservations) => {
        this.reservations = reservations;
        this.loading = false;
      },
      error: () => {
        this.reservations = [];
        this.errorMessage = 'Unable to load maintenance reservations.';
        this.loading = false;
      }
    });
  }

}
